import { EventEmitter, Injectable, inject } from '@angular/core';
import {
	HttpClient,
	HttpErrorResponse,
	HttpParams,
} from '@angular/common/http';
import { catchError, map, of } from 'rxjs';
import { environment } from '../environments/environment';
import type { Alias } from './interfaces/alias';
import type { Authority } from './interfaces/authority';
import type { Component } from './interfaces/component';
import type { Document } from './interfaces/document';
import type { DocumentComplete } from './interfaces/document-complete';
import type { Dossier } from './interfaces/dossier';
import type { DossierExpression } from './interfaces/dossier-expression';
import type { DossierType } from './interfaces/dossier-type';
import type { DocumentType } from './interfaces/document-type';
import type { DocumentSubtype } from './interfaces/document-subtype';
import type { Paginated } from './interfaces/paginated';
import type { Phase } from './interfaces/phase';
import type { Step } from './interfaces/step';
import type { Version } from './interfaces/version';
import type { Notification } from './interfaces/notification';
import type { Profile } from './interfaces/profile';
import type { Error } from './interfaces/error';
import type { StagingArea } from './interfaces/staging-area';

@Injectable({
	providedIn: 'root',
})
export class IterlexService {
	private http = inject(HttpClient);
	private profile!: Profile;

	public error$: EventEmitter<Error> = new EventEmitter<Error>();
	private handleError(
		error: HttpErrorResponse,
		error$: EventEmitter<Error>,
		additionalData?: Record<string, string>,
	) {
		console.error(error);
		if (error.error) {
			error$.emit({
				status: error.error.status,
				title: error.error.title,
				detail: error.error.detail,
				additionalData: additionalData,
			});
		} else {
			error$.emit({});
		}
		return of();
	}
	toCamelCase(string: string) {
		return string
			.toLowerCase()
			.replace(/[^a-zA-Z0-9]+(.)/g, (_, firstLetter) =>
				firstLetter.toUpperCase(),
			);
	}
	dateTimeWithTimeZone(date: string, time: string) {
		const timezoneOffset = -new Date().getTimezoneOffset();
		const timezone = `${timezoneOffset >= 0 ? '+' : ''}${Math.floor(
			timezoneOffset / 60,
		)
			.toString()
			.padStart(2, '0')}:${Math.floor(timezoneOffset % 60)
			.toString()
			.padStart(2, '0')}`;
		return `${date}T${time}:00${timezone}`;
	}
	getMainAlias(document: Document, body: string) {
		return (
			document.aliases.find((e) => e.uri.includes(body)) ?? {
				uri: document.workUri,
				number: document.number,
				date: document.date,
			}
		);
	}
	getAuthorities() {
		return this.http
			.get<Authority[]>(environment.api_url + '/iterlex/authorities')
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getDossierTypes(params: HttpParams) {
		return this.http
			.get<DossierType[]>(
				environment.api_url + '/iterlex/dossier-types',
				{
					params: params,
				},
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getDossiers(params: HttpParams) {
		return this.http
			.get<Paginated<Dossier[]>>(
				environment.api_url + '/iterlex/dossiers',
				{
					params: params,
				},
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	createDossier(
		authority: string,
		body: string,
		title: string,
		number: number,
		date: string,
		dossierType: string,
		metadata?: Dossier['metadata'],
	) {
		const newDossier = {
			authority: authority,
			body: body,
			country: 'it-bz',
			type: 'documentCollection',
			subtype: 'dossier',
			author: body,
			title: title,
			number: number,
			date: date,
			dossierType: dossierType,
			metadata: metadata,
		};
		return this.http
			.post<Dossier>(
				environment.api_url + '/iterlex/dossiers',
				newDossier,
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getDossier(authority: string, body: string, uri: string) {
		return this.http
			.get<Dossier>(environment.api_url + '/iterlex/dossiers', {
				params: new HttpParams()
					.append('authority', authority)
					.append('body', body)
					.append('workUri', uri),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	editDossier(
		uri: string,
		authority: string,
		body: string,
		newTitle: string,
		newState?: string,
	) {
		const updatedDossier = {
			newTitle: newTitle,
			newState: newState,
		};
		return this.http
			.put(environment.api_url + '/iterlex/dossiers', updatedDossier, {
				params: new HttpParams()
					.append('workUri', uri)
					.append('authority', authority)
					.append('body', body),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	deleteDossier(authority: string, body: string, uri: string) {
		return this.http
			.delete<void>(environment.api_url + '/iterlex/dossiers', {
				params: new HttpParams()
					.append('workUri', uri)
					.append('authority', authority)
					.append('body', body),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getDossierExpressions(authority: string, body: string, uri: string) {
		return this.http
			.get<DossierExpression[]>(
				environment.api_url + '/iterlex/dossier-expressions',
				{
					params: new HttpParams()
						.append('authority', authority)
						.append('body', body)
						.append('workUri', uri),
				},
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	createDossierExpression(
		authority: string,
		body: string,
		workUri: string,
		author: string,
		title: string,
		date: string,
		phaseName: string,
	) {
		const newDossierExpression = {
			authority: authority,
			body: body,
			workUri: workUri,
			language: 'mul',
			author: author,
			title: title,
			date: date,
			phaseId: phaseName,
		};
		return this.http
			.post<DossierExpression>(
				environment.api_url + '/iterlex/dossier-expressions',
				newDossierExpression,
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	addPhaseToDossierType(
		authority: string,
		body: string,
		dossierType: string,
		phaseName: string,
		order: number,
	) {
		const newPhaseInDossierType = {
			authority: authority,
			body: body,
			dossierType: dossierType,
			phaseId: phaseName,
			order: order,
		};
		return this.http
			.post(
				environment.api_url + '/iterlex/phases',
				newPhaseInDossierType,
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getPhases(authority: string, body: string, dossierType?: string) {
		let params = new HttpParams()
			.append('authority', authority)
			.append('body', body);
		if (dossierType) {
			params = params.append('dossierType', dossierType);
		}
		return this.http
			.get<Phase[]>(environment.api_url + '/iterlex/phases', {
				params: params,
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	editPhaseInDossierType(
		authority: string,
		body: string,
		dossierType: string,
		phaseName: string,
		order: number,
	) {
		const newPhaseInDossierType = {
			authority: authority,
			body: body,
			dossierType: dossierType,
			phaseId: phaseName,
			order: order,
		};
		return this.http
			.put(environment.api_url + '/iterlex/phases', newPhaseInDossierType)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	deletePhaseFromDossierType(
		authority: string,
		body: string,
		dossierType: string,
		phaseName: string,
	) {
		return this.http
			.delete(environment.api_url + '/iterlex/phases', {
				params: new HttpParams()
					.append('authority', authority)
					.append('body', body)
					.append('dossierType', dossierType)
					.append('phaseId', phaseName),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getSteps(
		authority: string,
		body: string,
		dossierUri: string,
		phaseName: string,
	) {
		return this.http
			.get<Step[]>(environment.api_url + '/iterlex/steps', {
				params: new HttpParams()
					.append('authority', authority)
					.append('body', body)
					.append('dossierUri', dossierUri)
					.append('phaseName', phaseName),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	createStep(
		dossierUri: string,
		authority: string,
		body: string,
		phaseName: string,
		descriptionITA: string,
		descriptionDEU: string,
		order: number,
		assignedBody?: string,
	) {
		const newStep = {
			dossierUri: dossierUri,
			authority: authority,
			body: body,
			phaseName: phaseName,
			id: assignedBody ?? this.toCamelCase(descriptionITA),
			descriptionITA: descriptionITA,
			descriptionDEU: descriptionDEU,
			order: order,
			assignedBody: assignedBody,
		};
		return this.http
			.post<Step>(environment.api_url + '/iterlex/steps', newStep)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	editStep(
		dossierUri: string,
		authority: string,
		body: string,
		phaseName: string,
		stepId: Step['id'],
		newDescriptionITA: string,
		newDescriptionDEU: string,
	) {
		const updatedStep = {
			authority: authority,
			body: body,
			dossierUri: dossierUri,
			phaseName: phaseName,
			stepId: stepId,
			newId: this.toCamelCase(newDescriptionITA),
			newDescriptionITA: newDescriptionITA,
			newDescriptionDEU: newDescriptionDEU,
		};
		return this.http
			.put(environment.api_url + '/iterlex/steps', updatedStep)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	deleteStep(
		authority: string,
		body: string,
		dossierUri: string,
		phaseName: string,
		id: Step['id'],
	) {
		return this.http
			.delete(environment.api_url + '/iterlex/steps', {
				params: new HttpParams()
					.append('authority', authority)
					.append('body', body)
					.append('dossierUri', dossierUri)
					.append('phaseName', phaseName)
					.append('stepId', id),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getDocumentTypes(params: HttpParams) {
		return this.http
			.get<DocumentType[]>(
				environment.api_url + '/iterlex/document-types',
				{
					params: params,
				},
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	createDocumentSubtype(
		authority: string,
		documentType: string,
		id: string,
		labelITA: string,
		labelDEU: string,
		order: number,
	) {
		const newDocumentSubtype = {
			authority: authority,
			documentType: documentType,
			id: id,
			labelITA: labelITA,
			labelDEU: labelDEU,
			order: order,
		};
		return this.http
			.post(
				environment.api_url + '/iterlex/document-subtypes',
				newDocumentSubtype,
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getDocumentSubtypes(params: HttpParams) {
		return this.http
			.get<DocumentSubtype[]>(
				environment.api_url + '/iterlex/document-subtypes',
				{
					params: params,
				},
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	editDocumentSubtype(
		authority: string,
		documentType: string,
		id: string,
		oldId: string,
		labelITA: string,
		labelDEU: string,
		order: number,
	) {
		const newDocumentSubtype = {
			authority: authority,
			documentType: documentType,
			id: id,
			oldId: oldId,
			labelITA: labelITA,
			labelDEU: labelDEU,
			order: order,
		};
		return this.http
			.put(
				environment.api_url + '/iterlex/document-subtypes',
				newDocumentSubtype,
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	deleteDocumentSubtype(authority: string, documentType: string, id: string) {
		return this.http
			.delete(environment.api_url + '/iterlex/document-subtypes', {
				params: new HttpParams()
					.append('authority', authority)
					.append('documentType', documentType)
					.append('id', id),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getDocuments(params: HttpParams) {
		return this.http
			.get<Paginated<Document[]>>(
				environment.api_url + '/iterlex/documents',
				{
					params: params,
				},
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	createDocument(
		authority: string,
		body: string,
		documenType: string,
		documentSubtype: string,
		author: string,
		date: string,
		number: string,
		title: string,
		dossierUri: string,
		hierarchy: string,
	) {
		const newDocument = {
			authority: authority,
			body: body,
			country: 'it-bz',
			type: documenType,
			subtype: documentSubtype,
			author: author,
			date: date,
			number: number,
			title: title,
			dossierUri: dossierUri,
			hierarchy: hierarchy,
		};
		return this.http
			.post<Document>(
				environment.api_url + '/iterlex/documents',
				newDocument,
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	editDocument(
		uri: string,
		authority: string,
		body: string,
		newTitle: string,
		newUri?: string,
	) {
		const updatedDocument = {
			newTitle: newTitle,
			newUri: newUri,
		};
		return this.http
			.put(environment.api_url + '/iterlex/documents', updatedDocument, {
				params: new HttpParams()
					.append('workUri', uri)
					.append('authority', authority)
					.append('body', body),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getVersions(params: HttpParams) {
		return this.http
			.get<Version[]>(environment.api_url + '/iterlex/versions', {
				params: params,
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	createComponent(
		authority: string,
		body: string,
		dossierUri: string,
		parentUri: string,
		childUri: string,
	) {
		const newComponent = {
			authority: authority,
			body: body,
			dossierUri: dossierUri,
			parentUri: parentUri,
			childUri: childUri,
		};
		return this.http
			.post<Component>(
				environment.api_url + '/iterlex/components',
				newComponent,
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	createVersion(
		documentUri: string,
		documentMainAliasUri: string,
		documentNumber: string,
		authority: string,
		body: string,
		language: string,
		title: string,
		date: string,
		author: string,
		metadata?: Version['metadata'],
	) {
		const newVersion = {
			authority: authority,
			body: body,
			workUri: documentUri,
			country: 'it-bz',
			type: documentMainAliasUri.split('/')[3],
			subtype: documentMainAliasUri.split('/')[4],
			workDate: documentMainAliasUri.split('/')[6],
			number: documentNumber,
			language: language,
			title: title,
			expressionDate: date,
			author: author,
			metadata: metadata,
		};
		return this.http
			.post<Version>(
				environment.api_url + '/iterlex/versions',
				newVersion,
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	deleteDocument(workUri: string, authority: string, body: string) {
		return this.http
			.delete(environment.api_url + '/iterlex/documents', {
				params: new HttpParams()
					.append('workUri', workUri)
					.append('authority', authority)
					.append('body', body),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	editVersion(
		expressionUri: string,
		authority: string,
		body: string,
		newMetadata: Version['metadata'],
	) {
		return this.http
			.put(
				environment.api_url + '/iterlex/versions',
				{
					newMetadata: newMetadata,
				},
				{
					params: new HttpParams()
						.append('expressionUri', expressionUri)
						.append('authority', authority)
						.append('body', body),
				},
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	deleteVersion(expressionUri: string, authority: string, body: string) {
		return this.http
			.delete(environment.api_url + '/iterlex/versions', {
				params: new HttpParams()
					.append('expressionUri', expressionUri)
					.append('authority', authority)
					.append('body', body),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	setVersionStep(
		authority: string,
		body: string,
		expressionUri: string,
		phaseName: string,
		stepId: Step['id'],
	) {
		const newVersionStep = {
			authority: authority,
			body: body,
			expressionUri: expressionUri,
			phaseName: phaseName,
			stepId: stepId,
		};
		return this.http
			.post<Version>(
				environment.api_url + '/iterlex/version/step',
				newVersionStep,
			)
			.pipe(
				catchError((err) =>
					this.handleError(err, this.error$, newVersionStep),
				),
			);
	}
	ignoreStepConstraints(
		authority: string,
		body: string,
		expressionUri: string,
		phaseName: string,
		stepId: Step['id'],
	) {
		const ignoreStepConstraints = {
			authority: authority,
			body: body,
			expressionUri: expressionUri,
			phaseName: phaseName,
			stepId: stepId,
		};
		return this.http
			.put(
				environment.api_url + '/iterlex/version/step/constraints',
				ignoreStepConstraints,
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getVersionSteps(expressionUri: string, authority: string, body: string) {
		return this.http
			.get<Step['id'][]>(environment.api_url + '/iterlex/version/step', {
				params: new HttpParams()
					.append('expressionUri', expressionUri)
					.append('authority', authority)
					.append('body', body),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	deleteVersionStep(
		expressionUri: string,
		authority: string,
		body: string,
		stepId: Step['id'],
	) {
		return this.http
			.delete(environment.api_url + '/iterlex/version/step', {
				params: new HttpParams()
					.append('expressionUri', expressionUri)
					.append('authority', authority)
					.append('body', body)
					.append('stepId', stepId),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	createFile(
		versionUri: string,
		authority: string,
		body: string,
		name: string,
		date: string,
		author: string,
		file: string,
	) {
		const newFile = {
			expressionUri: versionUri,
			authority: authority,
			body: body,
			name: name,
			date: date,
			author: author,
			file: file,
		};
		return this.http
			.post<File>(environment.api_url + '/iterlex/files', newFile, {
				reportProgress: true,
				observe: 'events',
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	editFile(
		versionUri: string,
		authority: string,
		body: string,
		name: string,
		newDate: string,
		newAuthor: string,
		newFile: string,
	) {
		const updatedFile = {
			expressionUri: versionUri,
			name: name,
			authority: authority,
			body: body,
			newDate: newDate,
			newAuthor: newAuthor,
			newFile: newFile,
		};
		return this.http
			.put<File>(environment.api_url + '/iterlex/files', updatedFile, {
				reportProgress: true,
				observe: 'events',
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	deleteFile(manifestationUri: string, authority: string, body: string) {
		return this.http
			.delete(environment.api_url + '/iterlex/files', {
				params: new HttpParams()
					.append('manifestationUri', manifestationUri)
					.append('authority', authority)
					.append('body', body),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	lockFile(
		manifestationUri: string,
		authority: string,
		body: string,
		roleId: string,
	) {
		const lockBody = {
			authority: authority,
			body: body,
			manifestationUri: manifestationUri,
			roleId: roleId,
		};
		return this.http
			.post(environment.api_url + '/iterlex/files/checkout', lockBody)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	unlockFile(
		manifestationUri: string,
		authority: string,
		body: string,
		roleId: string,
		name?: string,
		file?: string,
	) {
		const unlockBody = {
			authority: authority,
			body: body,
			manifestationUri: manifestationUri,
			roleId: roleId,
			name: name,
			file: file,
		};
		return this.http
			.post(environment.api_url + '/iterlex/files/checkin', unlockBody)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getDocumentComplete(
		authority: string,
		body: string,
		uri: string,
		phaseName: string,
		stepId: Step['id'],
		dossierUri?: string,
	) {
		return this.http
			.get<DocumentComplete>(environment.api_url + '/iterlex/document', {
				params: new HttpParams()
					.append('authority', authority)
					.append('body', body)
					.append('workUri', uri)
					.append('phaseName', phaseName)
					.append('stepId', stepId)
					.append('dossierUri', dossierUri ?? ''),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	downloadFile(manifestationUri: string, authority: string, body: string) {
		return this.http
			.get(environment.api_url + '/iterlex/file', {
				params: new HttpParams()
					.append('authority', authority)
					.append('body', body)
					.append('manifestationUri', manifestationUri),
				responseType: 'blob',
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	sendToStagingArea(
		authority: string,
		body: string,
		dossierUri: string,
		phaseName: string,
		stepName: string,
		documentExpressionUris: string[],
		recipientAuthorityName: string,
	) {
		const newDossierExpression = {
			authority: authority,
			body: body,
			dossierUri: dossierUri,
			phaseName: phaseName,
			stepName: stepName,
			documentExpressionUris: documentExpressionUris,
			recipientAuthorityName: recipientAuthorityName,
		};
		return this.http
			.post<DossierExpression>(
				environment.api_url + '/iterlex/staging-area',
				newDossierExpression,
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getStagingAreas(params: HttpParams) {
		return this.http
			.get<Paginated<StagingArea[]>>(
				environment.api_url + '/iterlex/staging-area',
				{
					params: params,
				},
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	deleteStagingArea(authority: string, body: string, stagingAreaId: number) {
		return this.http
			.delete<void>(environment.api_url + '/iterlex/staging-area', {
				params: new HttpParams()
					.append('authority', authority)
					.append('body', body)
					.append('stagingAreaId', stagingAreaId),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	importStagingArea(
		authority: string,
		body: string,
		stagingAreaId: number,
		dossierUri: string,
		newNumber: string,
		newDate: string,
		phaseName: string,
		stepName: string,
	) {
		const splittedDossierUri = dossierUri.split('/');
		const importDossierFromStagingArea = {
			authority: authority,
			body: body,
			stagingAreaId: stagingAreaId,
			country: splittedDossierUri[2],
			type: splittedDossierUri[3],
			subtype: splittedDossierUri[4],
			newAuthor: body,
			language: 'mul',
			newNumber: '' + newNumber,
			newDate: newDate,
			phaseName: phaseName,
			stepName: stepName,
		};
		return this.http
			.post<DossierExpression>(
				environment.api_url + '/iterlex/staging-area/dossier',
				importDossierFromStagingArea,
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	createDocumentAlias(
		authority: string,
		body: string,
		originalDocumentUri: string,
		documenType: string,
		documentSubtype: string,
		author: string,
		date: string,
		number: string,
	) {
		const newDocumentAlias = {
			authority: authority,
			body: body,
			originalWorkUri: originalDocumentUri,
			country: 'it-bz',
			type: documenType,
			subtype: documentSubtype,
			author: author,
			date: date,
			number: number,
		};
		return this.http
			.post<Alias>(
				environment.api_url + '/iterlex/alias',
				newDocumentAlias,
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	sendToBede(
		authority: string,
		body: string,
		dossierUri: string,
		phaseName: string,
		stepName: string,
		phaseNameOutput: string,
		stepNameOutput: string,
		mainManifestationUri: string,
		manifestationUris: string[],
	) {
		const bede = {
			dossierUri: dossierUri,
			phaseName: phaseName,
			stepName: stepName,
			phaseNameOutput: phaseNameOutput,
			stepNameOutput: stepNameOutput,
			mainManifestationUri: mainManifestationUri,
			manifestationUris: manifestationUris,
		};
		return this.http
			.put(environment.api_url + '/iterlex/bede', bede, {
				params: new HttpParams()
					.append('authority', authority)
					.append('body', body),
			})
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getNotifications(
		authority: string,
		body: string,
		showArchived: boolean,
		dossierUri?: string,
	) {
		let params = new HttpParams()
			.append('authority', authority)
			.append('body', body)
			.append('showArchived', showArchived);
		if (dossierUri) {
			params = params.append('dossierUri', dossierUri);
		}
		return this.http
			.get<Paginated<Notification[]>>(
				environment.api_url + '/iterlex/notifications',
				{
					params: params,
				},
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	editNotification(
		authority: string,
		body: string,
		id: number,
		read: boolean,
		archived: boolean,
	) {
		return this.http
			.put(
				environment.api_url + '/iterlex/notifications',
				{ read: read, archived: archived },
				{
					params: new HttpParams()
						.append('authority', authority)
						.append('body', body)
						.append('id', id),
				},
			)
			.pipe(catchError((err) => this.handleError(err, this.error$)));
	}
	getProfile() {
		if (!this.profile) {
			return this.http
				.get<Profile>(environment.api_url + '/iterlex/profile')
				.pipe(
					catchError((err) => this.handleError(err, this.error$)),
					map((profile) => {
						this.profile = profile;
						return profile;
					}),
				);
		} else {
			return of(this.profile);
		}
	}
}
